import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GetMeResponse, UpdatePasswordRequest } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useMeTranslation } from '../hooks';
import { addOrUpdateMeCache } from '../queries';

type UseUpdatePasswordActionData = {
    updatePassword: (data: UpdatePasswordRequest) => Promise<GetMeResponse>;
    loading: boolean;
    error: string;
};

/** Changing the signed-in user's own password. */
export function useUpdatePasswordAction(): UseUpdatePasswordActionData {
    const { t } = useMeTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error `PATCH /v3/me/password`
    // declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'INCORRECT_PASSWORD')) {
                return t('actions.update_password.error_wrong_password');
            }
            if (isApiError(error, 'USER_NOT_FOUND')) {
                return t('shared.errors.user_not_found');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (data: UpdatePasswordRequest) =>
            openApiRef.current.me
                .updatePassword(data)
                .then((response) => response.data),
        onSuccess: (data) => {
            addOrUpdateMeCache(queryClient, data.data);

            UIToast.success(t('actions.update_password.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        updatePassword: (data) => mutation.mutateAsync(data),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
